/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useState, useEffect, useCallback, useMemo, memo, Suspense } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dagre from "dagre";
import {
  ChevronLeft,
  File,
  RefreshCw,
  Folder,
  ChevronRight,
  FileSymlink,
  Copy,
  Download,
  Code,
  ChevronDown,
  Menu,
  X,
  MapPin,
  CalendarDays,
  Loader2,
  ExternalLink
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import ReactMarkdown from "react-markdown";
import FlowChart from "./Architecture";
import { apiMethods } from "@/utils/apis";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";

import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { CardContent } from "@/components/ui/card";
import { CardFooter } from "@/components/ui/card";
import { CardHeader } from "@/components/ui/card";
import { CardTitle } from "@/components/ui/card";
import { Tabs } from "@/components/ui/tabs";
import { TabsList } from "@/components/ui/tabs";
import { TabsTrigger } from "@/components/ui/tabs";
import { TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import MarkdownRenderer from "./Documentation";

// Types (unchanged)
interface RepoData {
  id: string;
  name: string;
  project_id: string;
  source: "github" | "local";
  repo_url?: string;
  created_at: number;
  updatedAt?: number;
  files: FileData[];
}

interface FileData {
  path: string;
  size?: number;
  modified?: string;
}

interface GenerationStatus {
  repository_id: string;
  status: string;
  last_generated_at?: number;
  documentation_available: boolean;
  architecture_available: boolean;
  documentation_record_id?: string;
  progress?: number;
}

interface DocumentationData {
  repository_id: string;
  documentation_id: string;
  filename: string;
  content: string;
  type: "documentation" | "architecture";
  generated_at: number;
}

interface LatestDocumentation {
  repository_id: string;
  latest_documentation?: { id: string; documentation_path: string; last_generated_at: number };
  latest_architecture?: { id: string; architecture_path: string; last_generated_at: number };
}

interface ArchitectureData {
  nodes: ArchitectureNode[];
  edges: ArchitectureEdge[];
  groups: ArchitectureGroup[];
  metadata: {
    title: string;
    description: string;
    technologies: string[];
    languages: string[];
    architecture_patterns: string[];
    key_features: string[];
    complexity_metrics: { coupling_degree: number; cohesion_degree: number; modularity_score: number };
  };
}

interface ArchitectureNode {
  id: string;
  type: string;
  name: string;
  description: string;
  file_path: string;
  language: string;
  dependencies: string[];
  size: number;
  tier: string;
  key_functions: string[];
  module_path: string;
  complexity: number;
  stability: number;
}

interface ArchitectureEdge {
  from: string;
  to: string;
  type: string;
  description: string;
  strength: number;
  bidirectional: boolean;
}

interface ArchitectureGroup {
  id: string;
  name: string;
  description: string;
  nodes: string[];
  color: string;
  tier: string;
  parent_group: string | null;
  is_core: boolean;
}

interface FileTreeNode {
  name: string;
  children: FileTreeNode[];
  isFolder: boolean;
  path?: string;
  size?: number;
  modified?: string;
}

// Cache Manager with better TTL management for documentation data
class CacheManager {
  private cache = new Map<string, { data: any; timestamp: number }>();
  private ttl = 5 * 60 * 1000; // 5 minutes TTL for better performance

  set(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  get(key: string): any | undefined {
    const item = this.cache.get(key);
    if (!item || (Date.now() - item.timestamp > this.ttl)) {
      this.cache.delete(key);
      return undefined;
    }
    return item.data;
  }

  clear(): void {
    this.cache.clear();
  }
}

const repoPageCache = new CacheManager();

// Utility functions (unchanged)
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (timestamp: number): string => {
  return new Date(timestamp).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const buildFileTree = (repoName: string, files: FileData[] = []): FileTreeNode => {
  const tree: FileTreeNode = { 
    name: repoName || "Unnamed Repository", 
    children: [], 
    isFolder: true,
    path: repoName 
  };
  
  files.forEach(file => {
    if (!file?.path) return;
    const parts = file.path.split('/').filter(Boolean);
    let current = tree;
    let currentPath = repoName;
    
    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1;
      currentPath = `${currentPath}/${part}`;
      
      if (isLast) {
        current.children.push({
          name: part,
          isFolder: false,
          size: file.size,
          modified: file.modified,
          path: currentPath,
          children: []
        });
      } else {
        let folder = current.children.find(child => child.name === part && child.isFolder);
        if (!folder) {
          folder = { name: part, children: [], isFolder: true, path: currentPath };
          current.children.push(folder);
        }
        current = folder;
      }
    });
  });

  const sortTreeNodes = (node: FileTreeNode) => {
    node.children.sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      return a.name.localeCompare(b.name);
    });
    node.children.forEach(sortTreeNodes);
  };
  
  sortTreeNodes(tree);
  return tree;
};

const getFileIcon = (filename: string) => {
  const extension = filename.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'js': case 'jsx': case 'ts': case 'tsx':
      return <File className="h-4 w-4 text-yellow-400" />;
    case 'css': case 'scss': case 'sass': case 'less':
      return <File className="h-4 w-4 text-blue-400" />;
    case 'html': case 'htm':
      return <File className="h-4 w-4 text-orange-400" />;
    case 'json':
      return <File className="h-4 w-4 text-green-400" />;
    case 'md':
      return <File className="h-4 w-4 text-gray-400" />;
    default:
      return <File className="h-4 w-4 text-[#00ff9d]" />;
  }
};

// Simplified LoadingSkeleton for better centering
const LoadingSkeleton = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-transparent z-50">
    <div className="flex flex-col items-center gap-4 p-8 rounded-xl bg-transparent border border-gray-300 dark:border-gray-700">
      <Loader2 className="h-10 w-10 animate-spin text-[#00e4c2]" />
      <span className="text-xl text-gray-800 dark:text-[#e6edf3]/90 font-medium tracking-wide">
        Processing...
      </span>
    </div>
  </div>
);

const FileTreeSkeleton = () => (
  <div className="p-2 space-y-2">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="flex items-center gap-2">
        <Skeleton className="h-4 w-4 bg-gray-200 dark:bg-[#1a1c23]" />
        <Skeleton className="h-4 w-[150px] bg-gray-200 dark:bg-[#1a1c23]" />
      </div>
    ))}
  </div>
);

const DocumentationSkeleton = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-transparent z-50">
  <div className="flex flex-col items-center gap-4 p-8 rounded-xl bg-transparent border border-gray-300 dark:border-gray-700">
    <Loader2 className="h-10 w-10 animate-spin text-[#00e4c2]" />
    <span className="text-xl text-gray-800 dark:text-[#e6edf3]/90 font-medium tracking-wide">
      Rendering your documentation
    </span>
  </div>
</div>
);

// Add this function near the other utility functions
const codeSyntaxTheme = () => {
  const isDarkMode = document.documentElement.classList.contains('dark');
  return isDarkMode ? oneDark : oneLight;
};

const RepoPage = () => {
  const { id: repoId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [activeTab, setActiveTab] = useState<"documentation" | "architecture">("documentation");
  const [isCopied, setIsCopied] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [notionConnectionStatus, setNotionConnectionStatus] = useState(null);
  const [isCheckingNotionConnection, setIsCheckingNotionConnection] = useState(false);
  const [isUploadingToNotion, setIsUploadingToNotion] = useState(false);
  
  const [state, setState] = useState<{
    projectId: string;
    repo: RepoData | null;
    isLoading: boolean;
    errors: Record<string, string>;
    hasFetched: boolean;
    selectedFile: string | null;
    expandedFolders: Set<string>;
    documentation: string | null;
    architectureData: ArchitectureData | null;
    isGeneratingDocs: boolean;
    generationStatus: GenerationStatus | null;
    latestDocs: LatestDocumentation | null;
  }>({
    projectId: '',
    repo: null,
    isLoading: true,
    errors: {},
    hasFetched: false,
    selectedFile: null,
    expandedFolders: new Set(),
    documentation: null,
    architectureData: null,
    isGeneratingDocs: false,
    generationStatus: null,
    latestDocs: null,
  });

  // Memoize the file tree computation to prevent unnecessary rebuilds
  const fileTree = useMemo(() => 
    state.repo?.files ? buildFileTree(state.repo.name, state.repo.files) : null
  , [state.repo?.name, state.repo?.files]);
  
  // Memoize the sidebar visibility calculation
  const shouldShowSidebar = useMemo(() => 
    !state.latestDocs?.latest_documentation || // No docs yet
    state.isGeneratingDocs ||                  // Generating docs
    activeTab === "architecture"               // Architecture tab active
  , [state.latestDocs?.latest_documentation, state.isGeneratingDocs, activeTab]);

  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validateToken = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return false;
    }
    return true;
  }, [navigate]);

  const fetchRepoData = useCallback(async (force = false) => {
    if (!validateToken() || !repoId) return;
    
    const repoCacheKey = `${repoId}:repo`;
    const filesCacheKey = `${repoId}:files`;
    if (!force && repoPageCache.get(repoCacheKey) && repoPageCache.get(filesCacheKey)) {
      const cachedRepo = repoPageCache.get(repoCacheKey) as RepoData;
      const cachedFiles = repoPageCache.get(filesCacheKey) as FileData[];
      setState(prev => ({
        ...prev,
        repo: { ...cachedRepo, files: cachedFiles },
        projectId: cachedRepo.project_id,
        isLoading: false,
        hasFetched: true,
        expandedFolders: new Set([cachedRepo.name]),
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const [repoData, filesData] = await Promise.all([
        apiMethods.getRepository(repoId),
        apiMethods.listRepofiles(repoId).catch(() => ({ files: [] })),
      ]);

      const updatedFiles = Array.isArray(filesData?.files) ? filesData.files : [];
      const updatedRepo: RepoData = { ...repoData, files: updatedFiles };

      repoPageCache.set(repoCacheKey, updatedRepo);
      repoPageCache.set(filesCacheKey, updatedFiles);

      setState(prev => ({
        ...prev,
        repo: updatedRepo,
        projectId: updatedRepo.project_id,
        isLoading: false,
        hasFetched: true,
        expandedFolders: new Set([updatedRepo.name]),
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error fetching repository";
      setState(prev => ({
        ...prev,
        errors: { ...prev.errors, fetch: errorMessage },
        isLoading: false,
        hasFetched: true,
      }));
      toast.error(errorMessage);
    }
  }, [repoId, validateToken]);

  const fetchDocumentation = useCallback(async (documentationId: string) => {
    if (!repoId) return;
    
    const cacheKey = `${repoId}:documentation:${documentationId}`;
    const cached = repoPageCache.get(cacheKey) as DocumentationData | undefined;

    if (cached) {
      setState(prev => ({ ...prev, documentation: cached.content }));
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true }));
      const docData = await apiMethods.downloadDocumentation(repoId, documentationId);
      repoPageCache.set(cacheKey, docData);
      setState(prev => ({ ...prev, documentation: docData.content, isLoading: false }));
    } catch (err) {
      setState(prev => ({ ...prev, isLoading: false }));
      toast.error("Failed to fetch documentation");
    }
  }, [repoId]);

  const fetchLatestDocumentation = useCallback(async () => {
    if (!repoId) return;
    
    const cacheKey = `${repoId}:latestDocs`;
    const cached = repoPageCache.get(cacheKey) as LatestDocumentation | undefined;
    
    if (cached) {
      setState(prev => ({ ...prev, latestDocs: cached }));
      if (cached.latest_documentation?.id) fetchDocumentation(cached.latest_documentation.id);
      // Don't automatically fetch architecture data - we'll do it on demand
      return;
    }

    try {
      const response = await apiMethods.getLatestDocumentation(repoId);
      const latestDocs: LatestDocumentation = {
        repository_id: repoId,
        latest_documentation: response.documentation_path ? {
          id: response.id,
          documentation_path: response.documentation_path,
          last_generated_at: response.last_generated_at
        } : undefined,
        latest_architecture: response.architecture_path ? {
          id: response.id,
          architecture_path: response.architecture_path,
          last_generated_at: response.last_generated_at
        } : undefined
      };

      repoPageCache.set(cacheKey, latestDocs);
      setState(prev => ({ ...prev, latestDocs }));

      if (latestDocs.latest_documentation) await fetchDocumentation(response.id);
      // Don't automatically fetch architecture data - will fetch on tab change
    } catch (err) {
      toast.error("Failed to fetch latest documentation");
    }
  }, [repoId, fetchDocumentation]);

  const generateDocumentation = useCallback(async () => {
    if (!repoId || state.isGeneratingDocs) return;
    
    setState(prev => ({ 
      ...prev, 
      isGeneratingDocs: true,
      generationStatus: { 
        repository_id: repoId, 
        status: "pending",
        documentation_available: false,
        architecture_available: false,
        progress: 0
      }
    }));
    toast.info("Starting documentation generation...");
    
    try {
      await apiMethods.generateDocumentation(repoId);
      const progress = (state.generationStatus?.progress || 0);
      setState(prev => ({ 
        ...prev, 
        generationStatus: { 
          ...prev.generationStatus!,
          status: "in_progress", 
          progress: progress + 5
        }
      }));
      checkGenerationStatus();
    } catch (err) {
      setState(prev => ({ ...prev, isGeneratingDocs: false }));
      toast.error("Failed to start documentation generation");
    }
  }, [repoId, state.isGeneratingDocs]);

  const checkGenerationStatus = useCallback(async (retry = 1) => {
    if (!repoId) return;
    
    try {
      const status = await apiMethods.checkGenerationStatus(repoId);
      const progress = status.status === "completed" ? 100 : 
                      status.status === "in_progress" ? 
                      Math.min((state.generationStatus?.progress || 0) + 10, 95) : 0;
      
      setState(prev => ({ ...prev, generationStatus: { ...status, progress } }));

      if (status.status === "completed" && status.documentation_available && status.architecture_available) {
        setState(prev => ({ ...prev, isGeneratingDocs: false }));
        toast.success("Documentation generated successfully!");
        
        if (status.documentation_record_id) {
          fetchDocumentation(status.documentation_record_id);
          // Don't fetch architecture automatically on completion - will fetch on tab switch
          
          const latestDocsKey = `${repoId}:latestDocs`;
          const updatedLatestDocs = {
            repository_id: repoId,
            latest_documentation: {
              id: status.documentation_record_id,
              documentation_path: `/documentation/${repoId}/${status.documentation_record_id}`,
              last_generated_at: Date.now()
            },
            latest_architecture: {
              id: status.documentation_record_id,
              architecture_path: `/architecture/${repoId}/${status.documentation_record_id}`,
              last_generated_at: Date.now()
            }
          };
          
          repoPageCache.set(latestDocsKey, updatedLatestDocs);
          setState(prev => ({ ...prev, latestDocs: updatedLatestDocs }));
        }
      } else if (status.status === "generating") {
        // Use increasing interval for subsequent checks (3s, 5s, 8s, 12s, etc.)
        const nextDelay = Math.min(3000 * (1 + 0.5 * retry), 15000);
        setTimeout(() => checkGenerationStatus(retry + 1), nextDelay);
      } else if (status.status !== "completed") {
        setState(prev => ({ ...prev, isGeneratingDocs: false }));
        toast.error("Generation failed");
      }
    } catch (err) {
      setState(prev => ({ ...prev, isGeneratingDocs: false }));
      toast.error("Failed to check generation status");
    }
  }, [repoId, state.generationStatus?.progress, fetchDocumentation]);

  const fetchArchitecture = useCallback(async (documentationId: string) => {
    if (!repoId) return;
    
    const cacheKey = `${repoId}:architecture:${documentationId}`;
    const cached = repoPageCache.get(cacheKey) as DocumentationData | undefined;

    if (cached) {
      setState(prev => ({ ...prev, architectureData: JSON.parse(cached.content) }));
      return;
    }

    try {
        setState(prev => ({ ...prev, isLoading: true }));
      const archData = await apiMethods.downloadArchitecture(repoId, documentationId);
      const parsedData = JSON.parse(archData.content);
      repoPageCache.set(cacheKey, archData);
      setState(prev => ({ ...prev, architectureData: parsedData, isLoading: false }));
    } catch (err) {
      setState(prev => ({ ...prev, isLoading: false }));
      toast.error("Failed to fetch architecture data");
    }
  }, [repoId]);

  const handleCopyMarkdown = async () => {
    if (!state.documentation) {
      toast.error("No documentation to copy");
      return;
    }
    
    setIsCopied(true);
    try {
      await navigator.clipboard.writeText(state.documentation);
      toast.success("Documentation copied to clipboard");
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy documentation");
      setIsCopied(false);
    }
  };

  const handleDownloadMarkdown = () => {
    if (!state.documentation) {
      toast.error("No documentation to download");
      return;
    }
    
    setIsDownloading(true);
    const filename = `${state.repo?.name || 'documentation'}_documentation.md`;
    const blob = new Blob([state.documentation], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setIsDownloading(false);
      toast.success("Documentation downloaded successfully");
    }, 100);
  };

  const checkNotionConnection = async () => {
    try {
      setIsCheckingNotionConnection(true);
      const response = await apiMethods.notionCheckToken();
      setNotionConnectionStatus(response);
      return response.status === "valid";
    } catch (error) {
      console.error("Error checking Notion connection:", error);
      toast.error("Failed to check Notion connection");
      return false;
    } finally {
      setIsCheckingNotionConnection(false);
    }
  };

  const handleUploadToNotion = async () => {
    // Prevent multiple uploads
    if (isCheckingNotionConnection || isUploadingToNotion) {
      return;
    }
    
    try {
      // First check if Notion is connected
      setIsCheckingNotionConnection(true);
      const isConnected = await checkNotionConnection();
      setIsCheckingNotionConnection(false);
      
      if (!isConnected) {
        // Redirect to Notion authorization
        const response = await apiMethods.notionAuthorize();
        // This should redirect to Notion OAuth flow
        window.location.href = response.url;
        return;
      }
      
      // If connected, trigger the upload but don't wait for completion
      if (state.repo?.id && state.documentation) {
        // Set loading state before API call
        setIsUploadingToNotion(true);
        
        // Show immediate feedback
        toast.success("Documentation upload started in background");
        
        try {
          // Make the API call
          const response = await apiMethods.notionUpload(
            state.repo.id, 
            { content: state.documentation, title: `Documentation for ${state.repo.name}` }
          );
          
          console.log("Upload initiated successfully:", response);
          
          // Keep button disabled for a few seconds to prevent accidental double-clicks
          setTimeout(() => {
            setIsUploadingToNotion(false);
          }, 5000);
        } catch (uploadError) {
          console.error("Error starting Notion upload:", uploadError);
          toast.error("Failed to start Notion upload");
          setIsUploadingToNotion(false);
        }
      } else {
        toast.error("No documentation available to upload");
      }
    } catch (error) {
      console.error("Error uploading to Notion:", error);
      toast.error("Failed to upload to Notion");
      setIsCheckingNotionConnection(false);
      setIsUploadingToNotion(false);
    }
  };

  useEffect(() => {
    if (repoId) {
      fetchRepoData();
      fetchLatestDocumentation();
    }
  }, [repoId, fetchRepoData, fetchLatestDocumentation]);
  
  // Optimize the tab change handler to prevent unnecessary re-renders
  const handleTabChange = useCallback((value: string) => {
    const newTab = value as "documentation" | "architecture";
    setActiveTab(newTab);
    
    // Only fetch architecture data when the user switches to that tab and data isn't already loaded
    if (newTab === "architecture" && !state.architectureData && state.latestDocs?.latest_architecture) {
      fetchArchitecture(state.latestDocs.latest_architecture.id);
    }
  }, [state.architectureData, state.latestDocs, fetchArchitecture]);

  const renderTreeNode = (node: FileTreeNode, depth = 0) => {
    const isExpanded = state.expandedFolders.has(node.path || "");
    const isSelected = state.selectedFile === node.path;
    
    const toggleFolder = (path: string) => {
      setState(prev => {
        const updated = new Set(prev.expandedFolders);
        if (updated.has(path)) updated.delete(path);
        else updated.add(path);
        return { ...prev, expandedFolders: updated };
      });
    };
    
    const selectFile = (path: string) => {
      setState(prev => ({ ...prev, selectedFile: path }));
    };
    
    return (
      <div key={node.path}>
        <div 
          className={`flex items-center py-1 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-[#1a1c23] ${
            isSelected ? "bg-blue-50 dark:bg-[#0d2f1a] text-blue-600 dark:text-[#00ff9d]" : ""
          } ${depth > 0 ? `ml-${depth * 2}` : ""}`}
          onClick={() => node.isFolder ? toggleFolder(node.path || "") : selectFile(node.path || "")}
        >
          {node.isFolder ? (
            isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" /> : 
                         <ChevronRight className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
          ) : (
            <span className="mr-1">{getFileIcon(node.name)}</span>
          )}
          <span className="truncate flex-1">{node.name}</span>
          {!node.isFolder && node.size !== undefined && (
            <span className="text-xs text-gray-500 ml-2">{formatFileSize(node.size)}</span>
          )}
        </div>
        
        {node.isFolder && isExpanded && (
          <div>{node.children.map(child => renderTreeNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  // When documentation changes, update the UI with it
  useEffect(() => {
    if (state.isGeneratingDocs) {
      return;
    }
    
    // Check for new documentation to load
    if (state.latestDocs?.latest_documentation?.id && !state.documentation) {
      fetchDocumentation(state.latestDocs.latest_documentation.id);
    }
  }, [state.latestDocs, state.documentation, state.isGeneratingDocs, fetchDocumentation]);

  // React.memo around MarkdownRenderer to prevent needless re-renders
  const MemoizedMarkdownRenderer = memo(MarkdownRenderer);

  if (state.isLoading && !state.hasFetched) {
    return (
      <div className="flex h-screen bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#e6edf3]">
        <LoadingSkeleton />
      </div>
    );
  }

  if (Object.keys(state.errors).length > 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#e6edf3] p-6">
        <Suspense fallback={<LoadingSkeleton />}>
          <Alert className="max-w-md bg-stone-50 dark:bg-[#1a1c23] border-red-500">
            <AlertDescription>{state.errors.fetch}</AlertDescription>
          </Alert>
          <Button
            className="mt-4 bg-[#00ff9d] text-black hover:bg-[#00cc7a]"
            onClick={() => {
              setState(prev => ({ ...prev, errors: {}, isLoading: true }));
              fetchRepoData(true);
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Retry
          </Button>
        </Suspense>
      </div>
    );
  }

      // Sidebar visibility is calculated at the top of the component using useMemo

  return (
    <div className="flex mt-4 h-screen bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#e6edf3] overflow-hidden">
      {/* File Explorer Sidebar - Conditionally Rendered */}
      {shouldShowSidebar && (
        <div
          className={`fixed md:relative w-72 h-full bg-white dark:bg-[#0d1117] border-r border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-in-out z-20 ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="font-semibold truncate">Files</span>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="md:hidden text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <ScrollArea className="flex-1">
              {fileTree ? renderTreeNode(fileTree) : <FileTreeSkeleton />}
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ease-in-out ${
          shouldShowSidebar && isSidebarOpen ? '' : 'w-full'
        }`}
      >
        <div className="border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between bg-white dark:bg-[#0d1117] sticky top-0 z-10">
          <div className="flex items-center">
            {shouldShowSidebar && (
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="md:hidden mr-4 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <Menu className="h-5 w-5" />
              </button>
            )}
            <div className="flex items-center">
              <button onClick={() => navigate(-1)} className="mr-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <h1 className="text-lg font-semibold truncate text-gray-900 dark:text-white">{state.repo?.name || "Repository"}</h1>
            </div>
          </div>
          <Suspense fallback={<Skeleton className="h-10 w-32 bg-gray-200 dark:bg-[#1a1c23]" />}>
            <Button
              onClick={generateDocumentation}
              disabled={state.isGeneratingDocs}
              className="bg-[#00ff9d] text-black hover:bg-[#00cc7a]"
            >
              {state.isGeneratingDocs ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : state.latestDocs?.latest_documentation ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate
                </>
              ) : (
                <>
                  <Code className="h-4 w-4 mr-2" />
                  Generate Docs
                </>
              )}
            </Button>
          </Suspense>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          {state.isGeneratingDocs ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
              <DocumentGenerationAnimation />
            </div>
          ) : state.latestDocs?.latest_documentation ? (
            <Suspense fallback={<LoadingSkeleton />}>
              <Tabs
                value={activeTab}
                onValueChange={handleTabChange}
                className="w-full h-full flex-1 flex flex-col"
              >
                <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 px-4 bg-white dark:bg-[#0d1117]">
                  <TabsList className="bg-transparent">
                    <TabsTrigger
                      value="documentation"
                      className="data-[state=active]:text-blue-600 dark:data-[state=active]:text-[#00ff9d] data-[state=active]:border-b-2 data-[state=active]:border-blue-600 dark:data-[state=active]:border-[#00ff9d]"
                    >
                      Documentation
                    </TabsTrigger>
                    <TabsTrigger
                      value="architecture"
                      className="data-[state=active]:text-blue-600 dark:data-[state=active]:text-[#00ff9d] data-[state=active]:border-b-2 data-[state=active]:border-blue-600 dark:data-[state=active]:border-[#00ff9d]"
                    >
                      Architecture
                    </TabsTrigger>
                  </TabsList>
                  <div className="flex items-center space-x-2">
                    {activeTab === "documentation" && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCopyMarkdown}
                          disabled={isCopied || !state.documentation}
                          className="border-gray-600 hover:bg-[#1a1c23]"
                        >
                          {isCopied ? (
                            <>Copied</>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-2" />
                              Copy
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadMarkdown}
                          disabled={isDownloading || !state.documentation}
                          className="border-gray-600 hover:bg-[#1a1c23]"
                        >
                          {isDownloading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleUploadToNotion}
                          disabled={isCheckingNotionConnection || isUploadingToNotion || !state.documentation}
                          className="border-gray-600 hover:bg-[#1a1c23]"
                        >
                          {isCheckingNotionConnection || isUploadingToNotion ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Upload to Notion
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <TabsContent value="documentation" className="mt-0 flex-1 overflow-hidden">
                  <ScrollArea className="h-full w-full">
                    <div className="p-6">
                      {state.documentation ? (
                        <div className="prose prose-invert max-w-none w-full">
                          <MemoizedMarkdownRenderer markdownContent={state.documentation} />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full w-full py-12">
                          <LoadingSkeleton />
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="architecture" className="mt-0 flex-1">
                  <ScrollArea className="h-full w-full">
                    <div className="p-6 h-full">
                      {state.architectureData ? (
                        <Suspense fallback={<LoadingSkeleton />}>
                          <div style={{ height: 800 }}>
                            <FlowChart testData={state.architectureData} />
                          </div>
                        </Suspense>
                      ) : state.isLoading ? (
                        <div className="flex items-center justify-center h-full w-full py-12">
                          <LoadingSkeleton />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-[#e6edf3]/80">
                          No architecture data available
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </Suspense>
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-6">
              <Suspense fallback={<LoadingSkeleton />}>
                <Card className="max-w-lg bg-stone-50 dark:bg-[#191d23] border border-gray-200 dark:border-none shadow-md hover:shadow-lg transition-shadow duration-300 rounded-lg">
                  <CardHeader>
                    <CardTitle className="text-gray-900 dark:text-white">Generate Documentation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-4 text-gray-700 dark:text-[#e6edf3]/90">
                      No documentation has been generated yet. Click below to start.
                    </p>
                    <ul className="space-y-2 mb-4 text-gray-700 dark:text-[#e6edf3]/90">
                      <li className="flex items-center">
                        <FileSymlink className="h-5 w-5 mr-2 text-blue-600 dark:text-[#00ff9d]" />
                        Comprehensive codebase documentation
                      </li>
                      <li className="flex items-center">
                        <Folder className="h-5 w-5 mr-2 text-blue-600 dark:text-[#00ff9d]" />
                        Architecture diagrams
                      </li>
                      <li className="flex items-center">
                        <Code className="h-5 w-5 mr-2 text-blue-600 dark:text-[#00ff9d]" />
                        Code structure analysis
                      </li>
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Button
                      onClick={generateDocumentation}
                      disabled={state.isGeneratingDocs}
                      className="w-full bg-[#00ff9d] text-black hover:bg-[#00cc7a]"
                    >
                      {state.isGeneratingDocs ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating
                        </>
                      ) : (
                        <>
                          <Code className="h-4 w-4 mr-2" />
                          Generate Documentation
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </Suspense>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(RepoPage);

// Simplified DocumentGenerationAnimation for better performance
const DocumentGenerationAnimation = () => {
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('Analyzing project structure...');

  // More efficient progress simulation with fewer state updates
  useEffect(() => {
    // Use fixed milestones instead of continuous updates
    const milestones = [
      { progress: 0, message: 'Analyzing project structure...' },
      { progress: 30, message: 'Generating documentation...' },
      { progress: 70, message: 'Finalizing...' },
      { progress: 100, message: 'Complete' }
    ];
    
    let currentIndex = 0;
    
    const updateProgress = () => {
      if (currentIndex < milestones.length) {
        const { progress, message } = milestones[currentIndex];
        setProgress(progress);
        setMessage(message);
        currentIndex++;
        
        // Dynamically adjust timing between steps
        const nextDelay = currentIndex === milestones.length - 1 ? 500 : 
                          currentIndex === 0 ? 800 : 2000;
        
        setTimeout(updateProgress, nextDelay);
      }
    };
    
    updateProgress();
    
    return () => {
      // Clean up any pending timers
      currentIndex = milestones.length;
    };
  }, []);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="bg-black bg-opacity-80 backdrop-blur-md rounded-lg max-w-md w-full mx-auto p-6 border border-gray-800 shadow-xl">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-green-400">&gt;_</span>
          <span className="text-white font-medium">Generating Documentation</span>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-gray-800 rounded-full mb-6">
          <div 
            className="h-full bg-green-400 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="text-center py-4">
          <div className="text-white opacity-80 flex items-center justify-center">
            <span>{message}</span>
            <span className="animate-pulse ml-1">_</span>
          </div>
        </div>
      </div>
    </div>
  );
};

